/**
 * Remote Commands Protocol
 *
 * Handles commands sent from command room to sector rooms for live management.
 * Commanders can pause rooms, kick participants, update modes, etc.
 */

import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'
import {
  uint8ArrayToBase64,
  base64ToUint8Array,
} from '../crypto/identity'

// Configure ed25519 to use sha512
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m))

/**
 * Remote command types
 */
export type RemoteCommandPayload =
  | { type: 'kick_participant'; targetPeerId: string; reason?: string }
  | { type: 'pause_room' }
  | { type: 'resume_room' }
  | { type: 'update_mode'; mode: 'mic' | 'chat' | 'poi'; value: string }
  | { type: 'update_boundary'; centerLat: number; centerLng: number; radiusMeters: number }
  | { type: 'clear_all_pois' }
  | { type: 'set_alert_status'; status: 'active' | 'alert' | 'moving' }

/**
 * Remote command message
 */
export interface RemoteCommand {
  type: 'remote_command'
  id: string
  commandRoomId: string
  sectorRoomId: string
  command: RemoteCommandPayload
  issuedBy: string
  issuedByName: string
  commanderPublicKey: string
  timestamp: Date
  signature: string
}

/**
 * Create a remote command
 */
export async function createRemoteCommand(
  commandRoomId: string,
  sectorRoomId: string,
  command: RemoteCommandPayload,
  issuedBy: string,
  issuedByName: string,
  commanderPrivateKey: Uint8Array,
  commanderPublicKey: Uint8Array
): Promise<RemoteCommand> {
  const id = crypto.randomUUID()
  const timestamp = new Date()

  // Create the payload to sign
  const payload = {
    type: 'remote_command',
    id,
    commandRoomId,
    sectorRoomId,
    command,
    issuedBy,
    timestamp: timestamp.toISOString(),
  }

  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
  const signature = await ed.signAsync(payloadBytes, commanderPrivateKey)

  return {
    type: 'remote_command',
    id,
    commandRoomId,
    sectorRoomId,
    command,
    issuedBy,
    issuedByName,
    commanderPublicKey: uint8ArrayToBase64(commanderPublicKey),
    timestamp,
    signature: uint8ArrayToBase64(signature),
  }
}

/**
 * Verify a remote command signature
 */
export async function verifyRemoteCommand(
  remoteCommand: RemoteCommand
): Promise<boolean> {
  try {
    const payload = {
      type: 'remote_command',
      id: remoteCommand.id,
      commandRoomId: remoteCommand.commandRoomId,
      sectorRoomId: remoteCommand.sectorRoomId,
      command: remoteCommand.command,
      issuedBy: remoteCommand.issuedBy,
      timestamp: remoteCommand.timestamp instanceof Date
        ? remoteCommand.timestamp.toISOString()
        : remoteCommand.timestamp,
    }

    const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
    const signature = base64ToUint8Array(remoteCommand.signature)
    const publicKey = base64ToUint8Array(remoteCommand.commanderPublicKey)

    return await ed.verifyAsync(signature, payloadBytes, publicKey)
  } catch {
    return false
  }
}

/**
 * Check if a command is for a specific sector
 */
export function isCommandForSector(
  command: RemoteCommand,
  sectorRoomId: string
): boolean {
  return command.sectorRoomId === sectorRoomId
}

/**
 * Check if a command is still valid (not expired)
 * Commands expire after 5 minutes
 */
export function isCommandValid(command: RemoteCommand): boolean {
  const MAX_AGE_MS = 5 * 60 * 1000 // 5 minutes
  const commandTime = command.timestamp instanceof Date
    ? command.timestamp.getTime()
    : new Date(command.timestamp).getTime()
  return Date.now() - commandTime < MAX_AGE_MS
}

/**
 * Serialize a remote command for transmission
 */
export function serializeRemoteCommand(command: RemoteCommand): Uint8Array {
  const json = JSON.stringify({
    ...command,
    timestamp: command.timestamp instanceof Date
      ? command.timestamp.toISOString()
      : command.timestamp,
  })
  return new TextEncoder().encode(json)
}

/**
 * Deserialize a remote command from transmission
 */
export function deserializeRemoteCommand(data: Uint8Array): RemoteCommand {
  const json = new TextDecoder().decode(data)
  const parsed = JSON.parse(json)

  return {
    ...parsed,
    timestamp: new Date(parsed.timestamp),
  }
}

/**
 * Get human-readable description of a command
 */
export function getCommandDescription(command: RemoteCommandPayload): string {
  switch (command.type) {
    case 'kick_participant':
      return `Kick participant${command.reason ? `: ${command.reason}` : ''}`
    case 'pause_room':
      return 'Pause room'
    case 'resume_room':
      return 'Resume room'
    case 'update_mode':
      return `Update ${command.mode} mode to ${command.value}`
    case 'update_boundary':
      return `Update boundary to ${command.radiusMeters}m radius`
    case 'clear_all_pois':
      return 'Clear all POI markers'
    case 'set_alert_status':
      return `Set status to ${command.status}`
  }
}

/**
 * Get urgency level of a command (for UI display)
 */
export function getCommandUrgency(command: RemoteCommandPayload): 'low' | 'medium' | 'high' {
  switch (command.type) {
    case 'kick_participant':
    case 'pause_room':
    case 'set_alert_status':
      return 'high'
    case 'update_boundary':
    case 'update_mode':
      return 'medium'
    case 'resume_room':
    case 'clear_all_pois':
      return 'low'
  }
}

/**
 * Create common commands helpers
 */
export const CommandHelpers = {
  pauseRoom: (
    commandRoomId: string,
    sectorRoomId: string,
    issuedBy: string,
    issuedByName: string,
    privateKey: Uint8Array,
    publicKey: Uint8Array
  ) => createRemoteCommand(
    commandRoomId,
    sectorRoomId,
    { type: 'pause_room' },
    issuedBy,
    issuedByName,
    privateKey,
    publicKey
  ),

  resumeRoom: (
    commandRoomId: string,
    sectorRoomId: string,
    issuedBy: string,
    issuedByName: string,
    privateKey: Uint8Array,
    publicKey: Uint8Array
  ) => createRemoteCommand(
    commandRoomId,
    sectorRoomId,
    { type: 'resume_room' },
    issuedBy,
    issuedByName,
    privateKey,
    publicKey
  ),

  kickParticipant: (
    commandRoomId: string,
    sectorRoomId: string,
    targetPeerId: string,
    issuedBy: string,
    issuedByName: string,
    privateKey: Uint8Array,
    publicKey: Uint8Array,
    reason?: string
  ) => createRemoteCommand(
    commandRoomId,
    sectorRoomId,
    { type: 'kick_participant', targetPeerId, reason },
    issuedBy,
    issuedByName,
    privateKey,
    publicKey
  ),

  updateMicMode: (
    commandRoomId: string,
    sectorRoomId: string,
    value: string,
    issuedBy: string,
    issuedByName: string,
    privateKey: Uint8Array,
    publicKey: Uint8Array
  ) => createRemoteCommand(
    commandRoomId,
    sectorRoomId,
    { type: 'update_mode', mode: 'mic', value },
    issuedBy,
    issuedByName,
    privateKey,
    publicKey
  ),

  updateBoundary: (
    commandRoomId: string,
    sectorRoomId: string,
    centerLat: number,
    centerLng: number,
    radiusMeters: number,
    issuedBy: string,
    issuedByName: string,
    privateKey: Uint8Array,
    publicKey: Uint8Array
  ) => createRemoteCommand(
    commandRoomId,
    sectorRoomId,
    { type: 'update_boundary', centerLat, centerLng, radiusMeters },
    issuedBy,
    issuedByName,
    privateKey,
    publicKey
  ),

  setAlertStatus: (
    commandRoomId: string,
    sectorRoomId: string,
    status: 'active' | 'alert' | 'moving',
    issuedBy: string,
    issuedByName: string,
    privateKey: Uint8Array,
    publicKey: Uint8Array
  ) => createRemoteCommand(
    commandRoomId,
    sectorRoomId,
    { type: 'set_alert_status', status },
    issuedBy,
    issuedByName,
    privateKey,
    publicKey
  ),
}

/**
 * Convert to CRDT entry format
 */
export function toRemoteCommandEntry(
  command: RemoteCommand
): {
  id: string
  commandRoomId: string
  sectorRoomId: string
  command: RemoteCommandPayload
  issuedBy: string
  timestamp: number
  signature: string
  executed: boolean
  executedAt?: number
} {
  return {
    id: command.id,
    commandRoomId: command.commandRoomId,
    sectorRoomId: command.sectorRoomId,
    command: command.command,
    issuedBy: command.issuedBy,
    timestamp: command.timestamp instanceof Date
      ? command.timestamp.getTime()
      : new Date(command.timestamp).getTime(),
    signature: command.signature,
    executed: false,
  }
}

/**
 * Convert from CRDT entry format
 */
export function fromRemoteCommandEntry(
  entry: {
    id: string
    commandRoomId: string
    sectorRoomId: string
    command: RemoteCommandPayload
    issuedBy: string
    timestamp: number
    signature: string
    executed: boolean
    executedAt?: number
  },
  issuedByName: string = 'Commander',
  commanderPublicKey: string = ''
): RemoteCommand {
  return {
    type: 'remote_command',
    id: entry.id,
    commandRoomId: entry.commandRoomId,
    sectorRoomId: entry.sectorRoomId,
    command: entry.command,
    issuedBy: entry.issuedBy,
    issuedByName,
    commanderPublicKey,
    timestamp: new Date(entry.timestamp),
    signature: entry.signature,
  }
}
